import styled, { css, keyframes } from "styled-components";

export const colors = {
  fender: {
    accessibleGold: '#A47000',
    black: '#000000',
    candyCola: '#A02729',
    grey01: '#F7F7F7',
    grey02: '#EFF0F0',
    grey03: '#E5E5E5',
    grey04: '#595959',
    grey05: '#333333',
    primaryDark: '#191A1F',
    white: '#FFFFFF',
  }
}

export const text = (weight: number, size: string, lineHeight: string, color: string) => css`
  font-weight: ${weight};
  font-size: ${size};
  line-height: ${lineHeight};
  color: ${color};
`;

export const font = (family: string, weight: number, size: string, lineHeight: string, letterSpacing?: string) => css`
  font-family: ${family};
  font-style: normal;
  font-weight: ${weight};
  font-size: ${size};
  line-height: ${lineHeight};
  letter-spacing: ${(letterSpacing) ? letterSpacing : 'normal'};
`;

export type ButtonStyle = |
  'Blue Pill' |
  'Black Pill' |
  'White Pill' |
  'Black Stroke Pill' |
  'White Stroke Pill' |
  'White Text' |
  'Black Text' |
  'Blue Text';

export const button = (style: ButtonStyle, brand: string, applySmallPillStyling = false) => css`
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-weight: 500;
  font-size: 16px;
  line-height: 20px;
  text-decoration: none;
  color: #fff;
  padding: 17px 50px;
  border-radius: 64px;
  transition: all .5s cubic-bezier(0,0,.23,1);

  ${style == 'Blue Pill' && `
    background-color: #0083FF;

    &:hover {
      background-color: #1878D2;
    }
  `}

  ${style == 'Black Pill' && `
    background-color: #1A1B1B;

    &:hover {
      background-color: #333333;
    }
  `}

  ${style == 'White Pill' && `
    color: #000;
    background-color: #FFF;

    &:hover {
      background-color: #EAEAEA;
    }
  `}

  ${style == 'Black Stroke Pill' && `
    color: #000;
    border: 1px solid #1A1B1B;

    &:hover {
      color: #fff;
      border-color: #333333;
      background-color: #333333;
    }
  `}

  ${style == 'White Stroke Pill' && `
    border: 1px solid #FFF;
    
    &:hover {
      color: #000;
      border-color: #EAEAEA;
      background-color: #EAEAEA;
    }
  `}

  ${applySmallPillStyling && style.includes('Pill') && `
    min-width: 120px;
    padding: 10px 15px;
  `}

  ${style.includes('Text') && `
    display: inline-block;
    font-weight: 600;
    padding: 10px 0;
  `}

  ${(style.includes('Text') && brand == 'presonus') && `
    &:after {
      content: '\\25ba';
      position: relative;
      display: inline-block;
      font-size: 10px;
      margin-left: 8px;
      bottom: 2px;
    }
  `}

  ${(style.includes('Text') && brand == 'fender') && `
    text-decoration: underline;
    letter-spacing: -0.02em;
  `}

  ${(style.includes('Pill') && brand == 'jackson') && `
    font-size: 1rem;
    font-weight: 600;
    text-transform: uppercase;
    border-radius: 0;
  `}

  ${style == 'Blue Text' && `
    color: #0083FF;
  `}

  ${style == 'White Text' && `
    color: #fff;
  `}

  ${style == 'Black Text' && `
    color: #000;
  `}
`;

export const buttonReset = () => css`
  appearance: none;
  background: none;
  border: none;
  padding: 0;
`;

export const MaxWidthContainer = styled.div`
  position: relative;
  margin: 0px auto;
  width: 90vw;
  max-width: 90vw;
`;

const pulse = keyframes`
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
`;

interface SkeletonProps {
  backgroundColor?: string;
  borderRadius?: string;
  height?: string;
  margin?: string;
  width?: string;
}

export const Skeleton = styled.div<SkeletonProps>`
  position: relative;
  z-index: 0;
  overflow: hidden;
  background-color: #fff;
  width: ${({ width = '100%' }): string => width};
  height: ${({ height = '1rem' }): string => height};
  border-radius: ${({ borderRadius = '1.5rem' }): string => borderRadius};
  ${({ margin }) => margin && `margin: ${margin}`};
  max-width: 100%;

  &:before {
    content: '';
    position: absolute;
    z-index: 0;
    top: 0;
    left: 0;
    height: 100%;
    width: 100%;
    background-color: ${({ backgroundColor = '#EFF0F0' }): string => backgroundColor};
    animation: ${pulse} 800ms ease-in-out infinite alternate;
    @media (prefers-reduced-motion) {
      animation: none;
    }
  }
`;